import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import Layout from "@/components/Layout";
import LoginPage from "@/pages/LoginPage";
import ChangePasswordPage from "@/pages/ChangePasswordPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import DashboardPage from "@/pages/DashboardPage";
import EmployeesPage from "@/pages/EmployeesPage";
import PayrollPage from "@/pages/PayrollPage";
import PayslipsPage from "@/pages/PayslipsPage";
import AdvancesPage from "@/pages/AdvancesPage";
import LeavePage from "@/pages/LeavePage";
import ReportsPage from "@/pages/ReportsPage";

import SettingsPage from "@/pages/SettingsPage";
import EmployeeProfilePage from "@/pages/EmployeeProfilePage";
import TrainingPage from "@/pages/TrainingPage";
import PerformancePage from "@/pages/PerformancePage";
import LeaveBalancesPage from "@/pages/LeaveBalancesPage";
import HolidaysPage from "@/pages/HolidaysPage";
import CalendarPage from "@/pages/CalendarPage";

function ProtectedRoute({ children, roles }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="animate-pulse text-sm text-slate-500">Loading...</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (user.must_change_password) return <Navigate to="/change-password" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/dashboard" replace />;
  return <Layout>{children}</Layout>;
}

function PublicRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="animate-pulse text-sm text-slate-500">Loading...</div>
      </div>
    );
  }
  if (user) return <Navigate to={user.must_change_password ? "/change-password" : "/dashboard"} replace />;
  return children;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
          <Route path="/change-password" element={<ChangePasswordPage />} />
          <Route path="/forgot-password" element={<PublicRoute><ForgotPasswordPage /></PublicRoute>} />
          <Route path="/reset-password" element={<PublicRoute><ResetPasswordPage /></PublicRoute>} />
          <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
          <Route path="/employees" element={<ProtectedRoute roles={["admin", "hr_manager"]}><EmployeesPage /></ProtectedRoute>} />
          <Route path="/employees/:id" element={<ProtectedRoute roles={["admin", "hr_manager"]}><EmployeeProfilePage /></ProtectedRoute>} />
          <Route path="/payroll" element={<ProtectedRoute roles={["admin", "hr_manager"]}><PayrollPage /></ProtectedRoute>} />
          <Route path="/payslips" element={<ProtectedRoute><PayslipsPage /></ProtectedRoute>} />
          <Route path="/advances" element={<ProtectedRoute roles={["admin", "hr_manager"]}><AdvancesPage /></ProtectedRoute>} />
          <Route path="/leave" element={<ProtectedRoute><LeavePage /></ProtectedRoute>} />
          <Route path="/training" element={<ProtectedRoute><TrainingPage /></ProtectedRoute>} />
          <Route path="/performance" element={<ProtectedRoute><PerformancePage /></ProtectedRoute>} />
          <Route path="/reports" element={<ProtectedRoute roles={["admin", "hr_manager"]}><ReportsPage /></ProtectedRoute>} />
          <Route path="/leave-balances" element={<ProtectedRoute roles={["admin", "hr_manager"]}><LeaveBalancesPage /></ProtectedRoute>} />
          <Route path="/holidays" element={<ProtectedRoute roles={["admin", "hr_manager"]}><HolidaysPage /></ProtectedRoute>} />
          <Route path="/calendar" element={<ProtectedRoute roles={["admin", "hr_manager"]}><CalendarPage /></ProtectedRoute>} />
          <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" />
    </AuthProvider>
  );
}

export default App;
